/** @file useScrub: drag a label to change a value (6 px per step, under 2 px is a click, Esc cancels). */
import type { KeyboardEvent, PointerEvent } from 'react';
import { useRef, useState } from 'react';
import { modifierScale } from './modifierScale';

const PX_PER_STEP = 6;
const CLICK_PX = 2;

/** The field's value and step, and what a drag does: `onChange` per move, `onEnd` on release, `onCancel` on Esc, `onClick` for a short press. */
export type ScrubOptions = {
  value: number; step: number; disabled: boolean;
  onChange: (value: number) => void; onEnd: (value: number) => void; onCancel: () => void; onClick: () => void;
};

/** The handlers to spread on the label element (it is captured and focused so Esc reaches it) and whether a drag is running. */
export type ScrubHandlers = {
  scrubbing: boolean;
  props: {
    onPointerDown: (e: PointerEvent<HTMLElement>) => void; onPointerMove: (e: PointerEvent<HTMLElement>) => void;
    onPointerUp: (e: PointerEvent<HTMLElement>) => void; onLostPointerCapture: (e: PointerEvent<HTMLElement>) => void;
    onKeyDown: (e: KeyboardEvent<HTMLElement>) => void;
  };
};

/** Captures (or releases) the pointer on `el`; asked by feature, since jsdom has no pointer capture. */
function capture(el: HTMLElement, id: number, on: boolean): void {
  if (!('setPointerCapture' in el)) return;
  if (on) el.setPointerCapture(id);
  else if (el.hasPointerCapture(id)) el.releasePointerCapture(id);
}

type Drag = { startX: number; lastX: number; acc: number; active: boolean };

/**
 * One step per 6 px (Shift x10, Alt x0.1, read at each move so a change of modifier never jumps the value); under 2 px
 * of travel the press is a click. The pointer is captured on the label; release ends the drag once, Esc cancels it.
 */
export function useScrub(o: ScrubOptions): ScrubHandlers {
  const drag = useRef<Drag | null>(null);
  const [scrubbing, setScrubbing] = useState(false);
  const stop = (e: { currentTarget: HTMLElement; pointerId?: number }): Drag | null => {
    const d = drag.current;
    drag.current = null;
    setScrubbing(false);
    if (e.pointerId !== undefined) capture(e.currentTarget, e.pointerId, false);
    return d;
  };
  const finish = (e: PointerEvent<HTMLElement>): void => {
    const d = stop(e);
    if (d === null) return;
    if (d.active) o.onEnd(d.acc); else o.onClick();
  };
  return {
    scrubbing,
    props: {
      onPointerDown: (e) => {
        if (o.disabled || e.button !== 0) return;
        e.preventDefault();
        capture(e.currentTarget, e.pointerId, true);
        e.currentTarget.focus();
        drag.current = { startX: e.clientX, lastX: e.clientX, acc: o.value, active: false };
      },
      onPointerMove: (e) => {
        const d = drag.current;
        if (d === null || (!d.active && Math.abs(e.clientX - d.startX) < CLICK_PX)) return;
        if (!d.active) { d.active = true; setScrubbing(true); }
        const steps = Math.trunc((e.clientX - d.lastX) / PX_PER_STEP);
        if (steps === 0) return;
        d.lastX += steps * PX_PER_STEP;
        d.acc = Number((d.acc + steps * o.step * modifierScale(e)).toPrecision(12));
        o.onChange(d.acc);
      },
      onPointerUp: finish,
      onLostPointerCapture: finish,
      onKeyDown: (e) => {
        if (e.code !== 'Escape' || drag.current === null) return;
        e.preventDefault();
        e.stopPropagation();
        if (stop({ currentTarget: e.currentTarget })?.active === true) o.onCancel();
      },
    },
  };
}
